import './Picture.scss';

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { PictureDefaultProps, PicturePropTypes } from './Picture.proptypes';
import useIntersectionObserver from './useIntersectionObserver';

const Picture = forwardRef(
  (
    {
      alt,
      altLoading,
      children,
      className,
      context,
      id,
      loader,
      observer,
      onError,
      onLoad,
      placeholder,
      rootMargin,
      src,
      srcSet,
      threshold,
      type,
    },
    ref,
  ) => {
    // Uniq id for srcSet
    const uniqueId = useMemo(() => {
      if (id && id !== '') {
        return id;
      }
      return `picture-${Date.now()}-${Math.random()}`;
    }, [id]);

    // Ref definition
    const innerRef = useRef(null);
    const pictureRef = ref ?? innerRef;

    // const loaderRef = useRef(null);
    const imageRef = useRef(null);

    // State src for images
    const [image, setImage] = useState(src);

    // State isVisible
    const [isVisible, setIsVisible] = React.useState(!observer);

    // Intersection Observer
    useIntersectionObserver({
      target: pictureRef,
      threshold,
      rootMargin,
      onIntersect: ([{ isIntersecting }], observerElement) => {
        if (isIntersecting) {
          setIsVisible(true);
          observerElement?.unobserve(pictureRef.current);
        }
      },
    });

    useEffect(() => {
      setImage(src);
    }, [src]);

    const classNames = useMemo(
      () =>
        ['picture', type ? `picture--${type}` : null, className]
          .filter((val) => val)
          .join(' '),
      [className, type],
    );

    const handleOnError = useCallback(() => {
      if (placeholder) {
        setImage(placeholder);
      } else {
        pictureRef.current.classList.add('picture--bg-placeholder');
        setImage(
          'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 315 315"></svg>',
        );
      }
      pictureRef.current.classList.add('picture--error');
      onError();
    }, [onError, pictureRef, placeholder]);

    const handleOnLoad = useCallback(() => {
      imageRef.current.classList.add('image--loaded');
      pictureRef.current.classList.add('picture--loaded');
      let ratioLitteral;
      const ratio = Math.ceil(
        (imageRef.current.naturalWidth / imageRef.current.naturalHeight) * 100,
      );
      const ratioStringified = ratio.toString();
      if (ratio > 0 && ratio < 10) {
        ratioLitteral = 'ratio-0';
      } else if (ratio >= 10 && ratio < 90) {
        ratioLitteral = `ratio-${ratioStringified[0]}0`;
      } else if (ratio >= 90 && ratio <= 110) {
        ratioLitteral = 'ratio-100';
      } else if (ratio > 110 && ratio < 1000) {
        ratioLitteral = `ratio-${ratioStringified[0]}00`;
      } else if (ratio >= 1000) {
        ratioLitteral = `ratio-${ratioStringified[0]}${ratioStringified[1]}00`;
      }
      if (ratioLitteral !== '') {
        pictureRef.current.dataset.ratio = ratioLitteral;
      }
      onLoad();
    }, [onLoad, pictureRef]);

    return (
      <div
        className={classNames}
        ref={pictureRef}
        id={uniqueId}
        data-qa-id={context || 'picture'}
      >
        {loader && (
          <img
            className="image--loader"
            src={loader}
            alt={altLoading}
            data-qa-id="image-loader"
          />
        )}
        <picture className="image__wrapper" data-qa-id="image-wrapper">
          {isVisible && (
            <>
              {srcSet &&
                srcSet.length &&
                srcSet.map((img, index) => {
                  const key = `${uniqueId}-${index}`;
                  return (
                    <source srcSet={img.srcset} media={img.media} key={key} />
                  );
                })}
              <img
                alt={alt}
                className="image"
                data-qa-id="image"
                onError={handleOnError}
                onLoad={handleOnLoad}
                ref={imageRef}
                src={image}
              />
            </>
          )}
        </picture>
        {children}
      </div>
    );
  },
);

Picture.defaultProps = {
  ...PictureDefaultProps,
};

Picture.propTypes = {
  ...PicturePropTypes,
};

export default Picture;
